import {
  ContainerController,
  PositionedModelController,
  PositionedModelOwnerController,
  VisualContainerElementController,
  VisualController,
  MediaContainerController,
  PromptContainerController,
  VisualElementController,
} from '@sas-dvr/ltjs-bird';
import { ltjsModule } from '@sas-dvr/ltjs-commons/ltjsEnvironment';
import {
  LayoutItem,
  LayoutState,
} from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { filterNulls } from './arrayUtils';
import { DimensionUnit, StyleChain } from '@sas-dvr/ltjs-commons';
import {
  getBackgroundColorString,
  getAllBorderStyles,
} from '@sas-dvr/ltjs-commons/util/StyleUtils';
import { MutableDict } from '@sas-dvr/internal-va-react-core/utility-types';
import { getScrollbarSize } from '@sas-dvr/internal-va-react-core/utils/scrollbarSize';

export function getKey(controller: PositionedModelController | VisualElementController): string {
  if (controller instanceof VisualElementController) {
    const name = controller.getVisualElementName();
    if (name) {
      return name;
    }
  } else if (controller instanceof VisualController) {
    const veController = controller.getVisualElementController();
    if (veController) {
      const name = veController.getVisualElementName();
      if (name) {
        return name;
      }
    }
  } else if (controller instanceof ContainerController) {
    const veController = controller.getVisualContainerElementController();
    if (veController) {
      const name = veController.getVisualElementName();
      if (name) {
        return name;
      }
    }
  }
  if (
    controller instanceof ContainerController ||
    controller instanceof VisualContainerElementController ||
    controller instanceof VisualController
  ) {
    //attempt sane id
    const model = controller.getModel();
    if (model) {
      const name = model.getName();
      if (name) {
        return name;
      }
    }
  }

  // FIXME: there are no guarantees that this is a stable key
  return ltjsModule.getClosetKey(controller.getClosetAddress()) + '';
}

export function getLayoutData(
  controller: PositionedModelController,
  childOverride?: PositionedModelController | null
): Partial<LayoutItem> {
  let childKeys: string[] | undefined;
  if (controller instanceof PositionedModelOwnerController) {
    if (childOverride) {
      childKeys = [getKey(childOverride)];
    } else {
      childKeys = filterNulls(controller.getOrderedVisualAndContainerControllers()).map((item) =>
        getKey(item)
      );
    }
  } else {
    childKeys = [];
  }
  let parentKey: string | undefined;
  if (controller instanceof VisualController) {
    const veName = controller
      .getCompositeParent()
      ?.getVisualContainerElementController()
      ?.getVisualElementName();
    if (typeof veName === 'string') {
      parentKey = veName;
    }
  }

  const data: {
    childKeys?: string[];
    style: LayoutItem['style'];
    compositeParentKey?: string;
  } = { childKeys, style: {}, compositeParentKey: parentKey };

  if (controller.isPaddingAvailable()) {
    const paddingDimension = controller.getPadding();
    if (paddingDimension) {
      data.style.padding = paddingDimension.getScalarValueForUnit(DimensionUnit.PIXELS, -1.0, 1.0);
    }
  }

  data.style.zIndex = controller.getZIndex();

  // FIXME: tricky logic that should be in portable.
  // Ported from VAViewerHTML BIRDTitledContent.applyPaddingFromController
  // Does the above implementation handle all the cases of the port below???
  //
  // let defaultPadding: string | null = null;
  // let padding: string | null = null;
  // let viElController: VisualElementController | null = null;
  // if (controller instanceof VisualController) {
  //   viElController = controller.getVisualElementController();
  //   defaultPadding = '3px';
  // } else if (controller instanceof PromptContainerController) {
  //   defaultPadding = '3px';
  // }
  //
  // if (controller.isPaddingAvailable()) {
  //   //
  //   // If there is a VisualElementController, then get any override
  //   // padding applied to the element container.
  //   // If there is no override then this padding from the report theme css is used.
  //   //
  //   if (viElController !== null) {
  //     var styleChain = viElController.getElementContainerStyleChain();
  //     if (styleChain) {
  //       const paddingDimension = styleChain.getStyleAttributeDimensionValue(StyleAttribute.PADDING, null);
  //       if (paddingDimension) {
  //         padding = paddingDimension.getScalarValueForUnit(DimensionUnit.PIXELS, -1.0, 1.0) + 'px';
  //       }
  //     }
  //   } else {
  //     //
  //     // If there is no VisualElementContainer, then get the padding
  //     // directly from the controller.
  //     //
  //     const paddingDimension = controller.getPadding();
  //     if (paddingDimension) {
  //       padding = paddingDimension.getScalarValueForUnit(DimensionUnit.PIXELS, -1.0, 1.0) + 'px';
  //     }
  //   }
  // } else if (defaultPadding) {
  //   padding = defaultPadding;
  // }
  //
  // if (padding) {
  //   data.padding = padding;
  // }
  let backgroundStyles: StyleChain | null = null;
  if (controller instanceof VisualController) {
    const vec = controller.getVisualElementController();
    if (vec) {
      backgroundStyles = vec.getBackgroundStyleChain();
    }
  } else if (controller instanceof ContainerController) {
    if (controller instanceof PromptContainerController) {
      const pcec = controller.getPromptContainerElementController();
      backgroundStyles = pcec && pcec.getBackgroundStyleChain();
    } else {
      backgroundStyles = controller.getContainerBackgroundStyleChain();
    }
  }
  if (backgroundStyles) {
    const bgColor = getBackgroundColorString(backgroundStyles);
    if (bgColor) {
      data.style.backgroundColor = bgColor;
    }
    const css = getAllBorderStyles(backgroundStyles, undefined);
    if (css) {
      if (css['border-color']) {
        data.style.borderColor = css['border-color'];
      }
      if (css['border-style']) {
        data.style.borderStyle = css['border-style'];
      }
      if (css['border-width']) {
        // If defined, border-width is always a number + 'px'
        const borderWidth: string = css['border-width'];
        data.style.borderWidth = parseFloat(borderWidth);
      }
    }
  }
  return data;
}

function getPositionForController(
  controller: PositionedModelController,
  isRoot: boolean
): Required<LayoutItem['position']> {
  const bounds = controller.getRelativeBounds();
  const position = {
    left: bounds[0],
    top: bounds[1],
    width: bounds[2],
    height: bounds[3],
  };
  if (isRoot) {
    return position;
  }
  // The bounds returned from portable include the parent's padding and border, so we need to subtract that here.
  const parent = controller.getParent();
  if (parent) {
    const borderDimension = parent.getBorderThickness();
    if (borderDimension) {
      const parentBorder = borderDimension.getScalarValueForUnit(DimensionUnit.PIXELS, -1.0, 1.0);
      position.left -= parentBorder;
      position.top -= parentBorder;
    }
    if (parent.isPaddingAvailable()) {
      const paddingDimension = parent.getPadding();
      if (paddingDimension) {
        const padding = paddingDimension.getScalarValueForUnit(DimensionUnit.PIXELS, -1.0, 1.0);
        position.left -= padding;
        position.top -= padding;
      }
    }
  }
  return position;
}

function getStateImpl(
  controllers: (PositionedModelController | null)[] | null,
  state: MutableDict<LayoutState>,
  isRoot = true
) {
  if (!controllers) {
    return;
  }
  for (const controller of controllers) {
    if (!controller) {
      continue;
    }
    const key = getKey(controller);
    state[key] = {
      position: getPositionForController(controller, isRoot),
    };
    if (controller instanceof PromptContainerController) {
      continue;
    }
    if (controller instanceof PositionedModelOwnerController) {
      state[key]!.scrollbars = getScrollbarState(controller).state;
      getStateImpl(controller.getOrderedVisualAndContainerControllers(), state, false);
    }
  }
}

export function getLayoutState(controller: PositionedModelController | null) {
  const map: LayoutState = {};
  if (!controller) {
    return map;
  }
  if (controller instanceof MediaContainerController) {
    getStateImpl(controller.getOrderedVisualAndContainerControllers(), map);
    map[getKey(controller)] = {
      scrollbars: getScrollbarState(controller).state,
    };
  } else {
    getStateImpl([controller], map);
  }
  return map;
}

export function getScrollbarState(
  controller: PositionedModelOwnerController,
  state?: LayoutItem['scrollbars']
) {
  let boundsChanged = false;
  let verticalScrollbarNeeded = false;
  let horizontalScrollbarNeeded = false;
  const children = controller.getOrderedVisualAndContainerControllers();
  if (!children) {
    return {
      layoutChanged: false,
      state,
    };
  }
  const { width: containerWidth, height: containerHeight } = getPositionForController(
    controller,
    false // isRoot does not affect width and height
  );
  // Some browsers (Safari) can get into an infinite loop without this check. With zero sized containers.
  //   If scrollbars won't fit, then don't try and add them.
  const scrollbarSize = getScrollbarSize();
  const scrollableWidth = containerWidth > scrollbarSize;
  const scrollableHeight = containerHeight > scrollbarSize;
  if (scrollableWidth || scrollableHeight) {
    for (
      let i = 0, len = children.length;
      (!verticalScrollbarNeeded || !horizontalScrollbarNeeded) && i < len;
      ++i
    ) {
      const child = children[i];
      if (!child) {
        continue;
      }
      const childBounds = getPositionForController(child, false);
      const bottom = childBounds.top + childBounds.height;
      // Account for rounding errors.
      if (scrollableHeight && bottom - containerHeight > 0.5) {
        verticalScrollbarNeeded = true;
      }
      if (childBounds.left < -0.5) {
        //
        // assume right-to-left with space inversion if left is negative
        // if left is negative, then it is more than a containerWidth away from
        // the right edge of the container.
        //
        horizontalScrollbarNeeded = true;
        continue;
      }
      const right = childBounds.left + childBounds.width;
      // Account for rounding errors.
      if (scrollableWidth && right - containerWidth > 0.5) {
        horizontalScrollbarNeeded = true;
      }
    }
  }
  if (
    (!state && verticalScrollbarNeeded) ||
    (state && verticalScrollbarNeeded !== state.vertical)
  ) {
    if (scrollbarSize) {
      boundsChanged = true;
    }
  }

  if (
    (!state && horizontalScrollbarNeeded) ||
    (state && horizontalScrollbarNeeded !== state.horizontal)
  ) {
    if (scrollbarSize) {
      boundsChanged = true;
    }
  }

  return {
    layoutChanged: boundsChanged,
    state: {
      horizontal: horizontalScrollbarNeeded,
      vertical: verticalScrollbarNeeded,
    },
  };
}
